import { cn, ensure } from "design/lib"
import type { ChangeEvent, ComponentProps } from "react"
import { useCallback, useEffect, useImperativeHandle, useRef } from "react"

type Props = ComponentProps<"textarea"> & {
  autoResize?: boolean
}

function Textarea({ ref, className, autoResize, onChange, ...props }: Props) {
  const ownRef = useRef<HTMLTextAreaElement>(null)
  useImperativeHandle(ref, () => ensure(ownRef.current))

  const resize = useCallback(() => {
    if (!autoResize) {
      return
    }
    const el = ownRef.current
    if (!el) {
      return
    }

    el.style.height = "auto"
    el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`
  }, [autoResize])

  useEffect(resize, [resize, props.value])

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    resize()
    onChange?.(e)
  }

  return (
    <textarea
      ref={ownRef}
      data-slot="textarea"
      className={cn(
        "flex field-sizing-content min-h-8 w-full rounded-lg border border-input bg-transparent px-2.5 py-1 text-base transition-colors outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:bg-input/50 disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-[3px] aria-invalid:ring-destructive/20 md:text-sm dark:bg-input/30 dark:disabled:bg-input/80 dark:aria-invalid:border-destructive/50 dark:aria-invalid:ring-destructive/40",
        autoResize && "max-h-[50vh] resize-none",
        className,
      )}
      onChange={handleChange}
      {...props}
    />
  )
}

export { Textarea }
